/**
 * @file utils/pageIntro/library.js
 * @copyright 2017 CBC/Radio-Canada. All rights reserved.
 * This file contains methods needed to fetch/display show banner and navigations on the site, e.g. the page intro
 */
import PageIntro from "../../components/PageIntro/component";
import { SECTIONS_WITH_BANNERS } from "../../constants/sections";
import { getPageIntroParams } from "../../utils/path/library";

/**
 * Returns the properties for the Show Intro section, like image, hosts etc.
 * @param {String} path path property
 * @param {object} intro Show details
 * @returns {object} The properties for PageIntro
 */
export const getIntroDetails = (path, intro) => {
    if (intro) {
        return {
            baseSection: intro.baseSection,
            subSection: intro.subSection,
            hasMenu: intro.hasMenu,
            hostName: intro.host || "",
            imageUrl: intro.image,
            imageLink: intro.imageLink || "",
            navigation:
                intro.navigation instanceof Array ? intro.navigation : [],
            path: path,
            showName: intro.title || "",
            description: intro.description || "",
            noHeading: false,
        };
    }

    // If the intro isn't in the config list
    return {
        section: "",
        subSection: "",
        hasMenu: true,
        hostName: "",
        imageUrl: "",
        imageLink: "",
        navigation: [],
        path: path,
        showName: intro ? intro.title : "",
        noHeading: false,
    };
};

/**
 * Returns the properties for the Page Intro section for detail pages
 * @param {String} props component properties
 * @returns {object} The page intro properties
 */
function getDetailPageIntro(path, baseSection, subSection) {
    let hasIntro = false;
    let parentPath = null;
    let hasMenu = true;
    for (let key in SECTIONS_WITH_BANNERS) {
        if (Object.prototype.hasOwnProperty.call(SECTIONS_WITH_BANNERS, key)) {
            const regex = new RegExp(`${key}.*-[0-9].[0-9]*`, "g");
            if (regex.test(path) && SECTIONS_WITH_BANNERS[key].showOnStories) {
                hasIntro = true;
                parentPath = key;
                hasMenu = Boolean(SECTIONS_WITH_BANNERS[key].showMenuOnStories);
                break;
            }
        }
    }

    if (hasIntro) {
        return getPageIntroParams(
            SECTIONS_WITH_BANNERS,
            parentPath,
            baseSection,
            subSection,
            hasMenu
        );
    }
    return null;
}

/**
 * Returns the properties for the Page Intro section for landing pages and subsections if enabled
 * @param {String} props component properties
 * @returns {object} The page intro properties
 */
function getLandingPageIntro(path, baseSection, subSection) {
    let hasIntro = false;
    let parentPath = null;
    let hasMenu = true;
    for (let key in SECTIONS_WITH_BANNERS) {
        if (Object.prototype.hasOwnProperty.call(SECTIONS_WITH_BANNERS, key)) {
            // Detect URL prefix
            const regex = new RegExp(`^${key}`);
            // Enable Intro for exact path or subsections path if config enabled
            const enableIntro =
                path === key ||
                (SECTIONS_WITH_BANNERS[key].showOnAllSubsections &&
                    regex.test(path));
            if (enableIntro) {
                hasIntro = true;
                parentPath = key;
                break;
            }
        }
    }

    if (hasIntro) {
        return getPageIntroParams(
            SECTIONS_WITH_BANNERS,
            parentPath,
            baseSection,
            subSection,
            hasMenu
        );
    }
    return null;
}

/**
 * Get sidebar widgets defined in constants, for the matching Landing page
 * E.g. for /sports/mlb get the MLB standings, etc.
 * @param {object} props - Component properties
 * @returns {object} widgetList - List of current widgets
 */
export const getPageIntro = ({
    path,
    baseSection,
    subSection,
    pageType,
    imageUrl,
    noHeading,
    rightrail,
    title,
}) => {
    let introParams = null;
    // Check props.pageType to determine which logic to use for pageIntro
    if (pageType === "detail") {
        introParams = getDetailPageIntro(path, baseSection, subSection);
    } else {
        introParams = getLandingPageIntro(path, baseSection, subSection);
    }
    if (introParams) {
        let pageIntroProps = getIntroDetails(path, introParams);

        if (imageUrl) {
            pageIntroProps.imageUrl = imageUrl;
        }

        // Get additional metadata from Polopoly eg. Banner
        pageIntroProps = insertPolopolyProps(pageIntroProps, rightrail);
        pageIntroProps.noHeading = noHeading || false;
        if (!pageIntroProps.showName) pageIntroProps.showName = title;

        return <PageIntro {...pageIntroProps} />;
    }

    return null;
};

/**
 * If imageURL does not exist in constant, get it from Polopoly props
 * @param {Object} intro Page Intro object from show/section constants
 * @param {Object} props Props that contains polopoly metadata from redux
 * @return PageIntro Object with updated Polopoly metadata
 */
export const insertPolopolyProps = (intro, rightrail) => {
    // Note: intro can be null even when Banner exist because it's turned off on Detail through "showOnStories" flag
    if (intro && !intro.imageUrl && rightrail?.banner) {
        intro.imageUrl = rightrail.banner;
    }
    return intro;
};
